<template>
  <DetailsLayout>
    <template #header>
      <AppHeader
        :title="offerte ? `${offerte.offerteNr}` : 'Offerte'"
        :show-back-button="true"
        back-to="/offertes"
        back-text="Alle offertes"
      />
    </template>

    <div class="max-w-8xl mx-auto overflow-y-auto py-8 pb-28 sm:px-2 lg:px-3">
      <!-- Loading State -->
      <div v-if="loading" class="flex min-h-64 items-center justify-center">
        <div class="text-center">
          <div class="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <p>Offerte laden...</p>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="flex min-h-64 items-center justify-center">
        <div class="text-center">
          <div class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg class="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <p class="font-medium text-red-600">Er is een fout opgetreden: {{ error }}</p>
        </div>
      </div>

      <!-- Offerte Details -->
      <template v-else-if="offerte">
        <div class="grid grid-cols-1 gap-4 lg:grid-cols-12">
          <div class="flex flex-col gap-2 lg:col-span-7">
            <!-- Hero Location Image -->
            <div v-if="fotoBoven?.remote_url" class="group relative mb-6">
              <!-- PhotoSwipe Gallery - Hidden but required for proper thumbnail animation -->
              <div ref="galleryRef" class="pswp-gallery">
                <a :href="fotoBoven?.remote_url" :data-pswp-width="1920" :data-pswp-height="1080" target="_blank">
                  <img :src="fotoBoven?.remote_url" alt="Locatie foto" />
                </a>
              </div>

              <div
                class="aspect-square min-h-64! w-full cursor-pointer overflow-hidden rounded-sm shadow-lg sm:aspect-square md:aspect-21/9"
                @click="openImageModal"
              >
                <img
                  ref="thumbnailRef"
                  :src="fotoBoven?.remote_url"
                  :alt="`Locatie foto van ${offerte.locatie?.naam || 'de locatie'}`"
                  class="hero-thumbnail h-full w-full cursor-pointer object-cover transition-transform duration-500 group-hover:scale-105"
                />

                <!-- Gradient overlay for better text readability -->
                <div class="absolute inset-0 bg-linear-to-t from-black/60 via-transparent to-transparent"></div>

                <!-- Status Badge -->
                <div class="absolute top-4 left-4">
                  <span
                    v-if="offerte"
                    :class="[
                      'inline-flex items-center rounded-sm bg-white/20 px-4 py-2 text-sm font-bold text-white outline-3 backdrop-blur-sm',
                      isAccepted ? 'outline-green-500/60' : isRejected ? 'outline-red-500/60' : 'outline-amber-500/60',
                    ]"
                  >
                    {{ statusLabel }}
                  </span>
                </div>

                <!-- Click indicator -->
                <div class="absolute top-4 right-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  <div class="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                    <svg class="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            <!-- Main Details Grid -->
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(16rem,1fr)]">
              <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <!-- Location Details Card -->
                <div class="border-background-200 bg-background-50 h-fit rounded-sm border p-4">
                  <div class="bg-background-100/80 sticky top-0 mb-4 flex items-center space-x-3 rounded-sm p-1">
                    <div class="border-background-400 flex size-10 items-center justify-center rounded-sm border">
                      <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </div>
                    <span class="text-lg font-semibold">Locatie</span>
                  </div>
                  <div class="divide-background-100 space-y-4 divide-y">
                    <div class="flex items-start justify-between">
                      <span class="text-sm font-medium">Naam</span>
                      <span class="max-w-[60%] text-right text-sm">{{ offerte.locatie?.naam || "-" }}</span>
                    </div>
                    <div class="flex items-start justify-between">
                      <span class="text-sm font-medium">Branche</span>
                      <span class="max-w-[60%] text-right text-sm">{{ offerte.locatie?.branche || "-" }}</span>
                    </div>
                    <div class="flex items-start justify-between">
                      <span class="text-sm font-medium">Adres</span>
                      <div class="max-w-[60%] text-right text-sm">
                        <div>{{ offerte.locatie?.straat }} {{ offerte.locatie?.huisnr }}</div>
                        <div>{{ offerte.locatie?.postcode }} {{ offerte.locatie?.plaats }}</div>
                      </div>
                    </div>
                    <div class="flex items-start justify-between">
                      <span class="text-sm font-medium">Bouwjaar</span>
                      <span class="max-w-[60%] text-right text-sm">{{ offerte.locatie?.bouwJaar || "-" }}</span>
                    </div>
                    <div class="flex items-start justify-between">
                      <span class="text-sm font-medium">Gebruiksdoel</span>
                      <span class="max-w-[60%] text-right text-sm">{{ offerte.locatie?.gebruiksDoel || "-" }}</span>
                    </div>
                    <div class="flex items-start justify-between">
                      <span class="text-sm font-medium">Oppervlakte</span>
                      <span class="max-w-[60%] text-right text-sm">{{ offerte.basis?.aM2 || 0 }} m²</span>
                    </div>
                    <div class="divide-background-100 space-y-2 divide-y p-2">
                      <div class="flex items-start justify-between">
                        <span class="text-sm font-medium">Contactpersoon</span>
                        <span class="max-w-[60%] text-right text-sm">{{ offerte.locatie?.contactNaam || "-" }}</span>
                      </div>
                      <div class="flex items-start justify-between">
                        <span class="text-sm font-medium">Email</span>
                        <a
                          v-if="offerte.locatie?.contactMail"
                          :href="`mailto:${offerte.locatie.contactMail}`"
                          class="text-primary-600 hover:text-primary-800 max-w-[60%] text-right text-sm break-all transition-colors"
                        >
                          {{ offerte.locatie.contactMail }}
                        </a>
                        <span v-else class="max-w-[60%] text-right text-sm">-</span>
                      </div>
                      <div class="flex items-start justify-between">
                        <span class="text-sm font-medium">Telefoon</span>
                        <a
                          v-if="offerte.locatie?.telnr"
                          :href="`tel:${offerte.locatie.telnr}`"
                          class="text-primary-600 hover:text-primary-800 text-sm transition-colors"
                        >
                          {{ offerte.locatie.telnr }}
                        </a>
                        <span v-else class="text-sm">-</span>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Installation Details Card -->
                <div class="border-background-200 bg-background-50 h-fit rounded-sm border p-4">
                  <div class="bg-background-100/80 sticky top-0 mb-4 flex items-center space-x-3 rounded-sm p-1">
                    <div class="border-background-400 flex size-10 items-center justify-center rounded-sm border">
                      <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                    </div>
                    <span class="text-lg font-semibold">Installatie</span>
                  </div>
                  <div class="divide-background-200 space-y-4 divide-y">
                    <div class="flex items-start justify-between">
                      <span class="text-sm font-medium">Aantal verdelers</span>
                      <span class="text-sm">{{ offerte.basis?.aVerdelers || "-" }}</span>
                    </div>
                    <div class="flex items-start justify-between">
                      <span class="text-sm font-medium">Jaar van aanleg</span>
                      <span class="text-sm">{{ offerte.locatie?.installatieJaar || "-" }}</span>
                    </div>
                    <!-- PV Installation Details -->
                    <div
                      v-if="
                        offerte.producten?.some(
                          (product: Product) =>
                            product.productType === 'scope12pi' || product.productType === 'scope12ebi',
                        )
                      "
                    >
                      <div class="mb-3 flex items-center space-x-2">
                        <div class="flex h-6 w-6 items-center justify-center rounded-md bg-yellow-100">
                          <svg class="h-3 w-3 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                            />
                          </svg>
                        </div>
                        <span class="text-md font-medium">PV-Installatie</span>
                      </div>
                      <div class="mb-2 flex items-start justify-between">
                        <span class="text-sm font-medium">PV Jaar van aanleg</span>
                        <span class="text-sm">{{ offerte.locatie?.pvInstallatieJaar || "-" }}</span>
                      </div>
                      <div class="ml-8 space-y-3">
                        <div class="flex items-start justify-between">
                          <span class="text-sm font-medium">Capaciteit</span>
                          <span class="text-sm">{{ offerte.basis?.pvKwp || "-" }} kWp</span>
                        </div>
                        <div class="flex items-start justify-between">
                          <span class="text-sm font-medium">Panelen</span>
                          <span class="text-sm">{{ offerte.basis?.pvPanelen || "-" }}</span>
                        </div>
                        <div class="flex items-start justify-between">
                          <span class="text-sm font-medium">Omvormers</span>
                          <span class="text-sm">{{ offerte.basis?.pvOmvormers || "-" }}</span>
                        </div>
                        <div class="flex items-start justify-between">
                          <span class="text-sm font-medium">Strengen</span>
                          <span class="text-sm">{{ offerte.basis?.pvStrengen || "-" }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <!-- Opdrachtgever Details Card -->
                <div class="border-background-200 bg-background-50 h-fit rounded-sm border p-4">
                  <div class="bg-background-100/80 sticky top-0 mb-4 flex items-center space-x-3 rounded-sm p-1">
                    <div class="border-background-400 flex size-10 h-10 items-center justify-center rounded-sm border">
                      <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    </div>
                    <span class="text-lg font-semibold">Opdrachtgever</span>
                  </div>
                  <div class="divide-background-200 space-y-4 divide-y">
                    <div class="flex items-start justify-between">
                      <span class="text-sm font-medium">Bedrijfsnaam</span>
                      <span class="max-w-[60%] text-right text-sm">{{
                        offerte.locatie?.opdrachtgever?.naam || "-"
                      }}</span>
                    </div>
                    <div class="flex items-start justify-between">
                      <span class="text-sm font-medium">Contactpersoon</span>
                      <span class="max-w-[60%] text-right text-sm">{{
                        offerte.locatie?.opdrachtgever?.contactnaam || "-"
                      }}</span>
                    </div>
                    <div class="flex items-start justify-between">
                      <span class="text-sm font-medium">Adres</span>
                      <div class="max-w-[60%] text-right text-sm">
                        <div>
                          {{ offerte.locatie?.opdrachtgever?.straat }} {{ offerte.locatie?.opdrachtgever?.huisnr }}
                        </div>
                        <div>
                          {{ offerte.locatie?.opdrachtgever?.postcode }} {{ offerte.locatie?.opdrachtgever?.plaats }}
                        </div>
                      </div>
                    </div>
                    <div class="flex items-start justify-between">
                      <span class="text-sm font-medium">Email</span>
                      <a
                        v-if="offerte.locatie?.opdrachtgever?.user?.email"
                        :href="`mailto:${offerte.locatie.opdrachtgever.user.email}`"
                        class="text-primary-600 hover:text-primary-800 max-w-[60%] text-right text-sm break-all transition-colors"
                      >
                        {{ offerte.locatie.opdrachtgever.user.email }}
                      </a>
                      <span v-else class="max-w-[60%] text-right text-sm">-</span>
                    </div>
                    <div class="flex items-start justify-between">
                      <span class="text-sm font-medium">Telefoon</span>
                      <a
                        v-if="offerte.locatie?.opdrachtgever?.telnr"
                        :href="`tel:${offerte.locatie.opdrachtgever.telnr}`"
                        class="text-primary-600 hover:text-primary-800 text-sm transition-colors"
                      >
                        {{ offerte.locatie.opdrachtgever.telnr }}
                      </a>
                      <span v-else class="max-w-[60%] text-right text-sm">-</span>
                    </div>
                  </div>
                </div>
                <!-- Inspection Company Card -->
                <div class="border-background-200 bg-background-50 h-fit rounded-sm border p-4">
                  <div class="bg-background-100/80 sticky top-0 mb-4 flex items-center space-x-3 rounded-sm p-1">
                    <div class="border-background-400 flex size-10 items-center justify-center rounded-sm border">
                      <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                    </div>
                    <span class="text-lg font-semibold">Inspectiebureau</span>
                  </div>
                  <div v-if="offerte.ibedrijf" class="divide-background-200 space-y-4 divide-y">
                    <div class="flex items-start justify-between">
                      <span class="text-sm font-medium">Bedrijfsnaam</span>
                      <span class="max-w-[60%] text-right text-sm">{{
                        offerte.ibedrijf.users?.display_name || offerte.ibedrijf.contactnaam || "-"
                      }}</span>
                    </div>
                    <div class="flex items-start justify-between">
                      <span class="text-sm font-medium">Contactpersoon</span>
                      <span class="max-w-[60%] text-right text-sm">{{ offerte.ibedrijf.contactnaam || "-" }}</span>
                    </div>
                    <div class="flex items-start justify-between">
                      <span class="text-sm font-medium">Adres</span>
                      <div class="max-w-[60%] text-right text-sm">
                        <div>{{ offerte.ibedrijf.straat }} {{ offerte.ibedrijf.huisnr }}</div>
                        <div>{{ offerte.ibedrijf.postcode }} {{ offerte.ibedrijf.plaats }}</div>
                      </div>
                    </div>
                    <div class="flex items-start justify-between">
                      <span class="text-sm font-medium">Telefoon</span>
                      <a
                        v-if="offerte.ibedrijf.telnr"
                        :href="`tel:${offerte.ibedrijf.telnr}`"
                        class="text-primary-600 hover:text-primary-800 text-sm transition-colors"
                      >
                        {{ offerte.ibedrijf.telnr }}
                      </a>
                      <span v-else class="max-w-[60%] text-right text-sm">-</span>
                    </div>
                    <div class="flex items-start justify-between">
                      <span class="text-sm font-medium">Website</span>
                      <a
                        v-if="offerte.ibedrijf.website && offerte.ibedrijf.website !== 'nvt'"
                        :href="
                          offerte.ibedrijf.website.startsWith('http')
                            ? offerte.ibedrijf.website
                            : `https://${offerte.ibedrijf.website}`
                        "
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-primary-600 hover:text-primary-800 max-w-[60%] text-right text-sm break-all transition-colors"
                      >
                        {{ offerte.ibedrijf.website }}
                      </a>
                      <span v-else class="max-w-[60%] text-right text-sm">-</span>
                    </div>
                    <div v-if="offerte.ibedrijf.scios_reg" class="flex items-start justify-between">
                      <span class="text-sm font-medium">SCIOS Registratie</span>
                      <span class="max-w-[60%] text-right text-sm">{{ offerte.ibedrijf.scios_reg }}</span>
                    </div>
                  </div>
                  <div v-else class="text-muted text-sm italic">Geen inspectiebureau informatie beschikbaar</div>
                </div>
              </div>
            </div>
          </div>
          <!-- Investment Section -->
          <div v-if="getConfirmedSubtotal() > 0" class="flex flex-col gap-3 lg:col-span-5">
            <!-- Investment Section -->
            <div class="border-background-200 bg-background-0 rounded-sm border p-4">
              <div class="bg-background-100/80 mb-4 flex items-center space-x-3 rounded-sm p-1">
                <div class="border-background-400 flex size-10 items-center justify-center rounded-sm border">
                  <svg class="size-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                    />
                  </svg>
                </div>
                <span class="text-lg font-semibold">Investering</span>
              </div>
              <div class="space-y-4">
                <!-- Pricing breakdown based on confirmed (aangepast) products only -->
                <template v-if="getConfirmedSubtotal() > 0">
                  <!-- Individual Product Breakdown -->
                  <div class="space-y-2">
                    <h4 class="mb-3 text-sm font-medium">Prijsoverzicht</h4>
                    <div
                      v-for="product in getConfirmedProducts()"
                      :key="`${product.productType}-${product.prijsType}`"
                      class="bg-background-50 flex items-center justify-between rounded-sm px-3 py-2"
                    >
                      <div class="flex items-center space-x-2">
                        <span class="text-muted text-sm font-medium">
                          {{ getProductLabel(product.productType) }}
                        </span>
                        <span v-if="product.aantal && product.aantal > 1" class="text-xs">
                          ({{ product.aantal }}x)
                        </span>
                      </div>
                      <span class="text-muted font-mono text-sm font-semibold">
                        {{ formatCurrency((product.prijs || 0) * (product.aantal || 1)) }}
                      </span>
                    </div>
                  </div>

                  <!-- Subtotal -->
                  <div
                    :class="[
                      'flex items-center justify-between border-t border-b py-3 text-gray-900',
                      // Make prominent if NO discount
                      !(offerte.korting && offerte.kortingBedrag !== null && offerte.kortingBedrag !== undefined)
                        ? 'border-primary-200 bg-primary-50 -mx-6 rounded-sm border px-6'
                        : 'border-background-200',
                    ]"
                  >
                    <span
                      :class="[
                        !(offerte.korting && offerte.kortingBedrag !== null && offerte.kortingBedrag !== undefined)
                          ? 'text-base font-semibold'
                          : 'text-base font-medium',
                      ]"
                      >Subtotaal (excl. BTW)</span
                    >
                    <span
                      :class="[
                        'font-mono',
                        !(offerte.korting && offerte.kortingBedrag !== null && offerte.kortingBedrag !== undefined)
                          ? 'text-xl font-semibold'
                          : 'text-lg font-semibold',
                      ]"
                      >{{ formatCurrency(getConfirmedSubtotal()) }}</span
                    >
                  </div>

                  <!-- Discount Section -->
                  <template
                    v-if="offerte.korting && offerte.kortingBedrag !== null && offerte.kortingBedrag !== undefined"
                  >
                    <div class="border-background-100 flex items-center justify-between border-b py-3">
                      <span class="text-base font-medium text-emerald-700">
                        Korting ({{ formatNumber(offerte.kortingPerc || 0) }}%)
                      </span>
                      <span class="font-mono text-lg font-semibold text-emerald-700">
                        - {{ formatCurrency(offerte.kortingBedrag) }}
                      </span>
                    </div>
                    <div
                      class="bg-primary-50 border-primary-200 -mx-6 flex items-center justify-between rounded-sm border px-6 py-4"
                    >
                      <span class="text-base font-semibold text-gray-900">Subtotaal na korting</span>
                      <span class="text-primary-700 font-mono text-xl font-semibold">{{
                        formatCurrency(offerte.totaalIncKorting)
                      }}</span>
                    </div>
                  </template>

                  <!-- VAT Calculation -->
                  <div class="border-background-100 flex items-center justify-between border-b text-sm">
                    <span class="font-medium">BTW (21%)</span>
                    <span>{{ formatCurrency(getVatAmount()) }}</span>
                  </div>

                  <!-- Final Total -->
                  <div class="-mt-2 flex items-center justify-between text-sm">
                    <span class="font-medium">Totaal incl. BTW</span>
                    <span class="font-semibold">{{ formatCurrency(getFinalTotal()) }}</span>
                  </div>
                </template>

                <!-- No pricing available -->
                <template v-else>
                  <div class="bg-background-50 border-background-200 rounded-sm border p-4">
                    <div class="flex items-center space-x-3">
                      <svg class="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span class="text-sm text-gray-600">Geen prijsinformatie beschikbaar</span>
                    </div>
                  </div>
                </template>
              </div>
            </div>

            <!-- Terms & Conditions and Inspection Plan Section -->
            <div
              v-if="
                (offerte.voorwaarden && offerte.voorwaarden.length > 0) ||
                (offerte.iplan && Object.keys(offerte.iplan).length > 0)
              "
              class="border-background-200 bg-background-0 rounded-sm border p-4"
            >
              <div class="bg-background-100/80 sticky top-0 mb-4 flex items-center space-x-3 rounded-sm p-1">
                <div class="border-background-400 flex size-10 items-center justify-center rounded-sm border">
                  <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <span class="text-lg font-semibold">Voorwaarden & Inspectieplan</span>
              </div>
              <Accordion class="w-full">
                <!-- Terms & Conditions Panel -->
                <AccordionPanel v-if="offerte.voorwaarden && offerte.voorwaarden.length > 0" value="voorwaarden">
                  <AccordionHeader>
                    <div class="flex items-center space-x-2">
                      <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <span class="font-medium">Voorwaarden</span>
                    </div>
                  </AccordionHeader>
                  <AccordionContent>
                    <div class="space-y-4 pt-4">
                      <div
                        v-for="(voorwaarde, index) in offerte.voorwaarden"
                        :key="index"
                        class="bg-background-50 border-background-200 rounded-sm border p-4"
                      >
                        <h4 class="mb-3 flex items-start text-base font-semibold">
                          <span
                            class="mt-0.5 mr-2 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-orange-100 text-xs font-bold text-orange-600"
                          >
                            {{ (index as number) + 1 }}
                          </span>
                          {{ voorwaarde.omschrijving }}
                        </h4>
                        <ul class="ml-7 space-y-1">
                          <li
                            v-for="waarde in voorwaarde.waarde"
                            :key="waarde"
                            class="flex items-start text-sm leading-relaxed"
                          >
                            <span class="mt-2 mr-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-orange-400"></span>
                            {{ waarde }}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionPanel>

                <!-- Inspection Plan Panel -->
                <AccordionPanel v-if="offerte.iplan && Object.keys(offerte.iplan).length > 0" value="inspectieplan">
                  <AccordionHeader>
                    <div class="flex items-center space-x-2">
                      <svg class="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 5H7a2 2 0 00-2 2v6a2 2 0 002 2h6a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                        />
                      </svg>
                      <span class="font-medium">Inspectieplan</span>
                    </div>
                  </AccordionHeader>
                  <AccordionContent>
                    <div class="space-y-6 pt-4">
                      <!-- General Section -->
                      <div
                        v-if="offerte.iplan.algemeen"
                        class="bg-background-50 border-background-200 rounded-sm border p-4"
                      >
                        <h4 class="mb-3 flex items-center text-base font-semibold">
                          <svg class="mr-2 h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          {{ offerte.iplan.algemeen.title || "Algemeen" }}
                        </h4>
                        <div v-if="offerte.iplan.algemeen.sections" class="space-y-3">
                          <div
                            v-for="(section, sectionIndex) in offerte.iplan.algemeen.sections"
                            :key="sectionIndex"
                            class="ml-6"
                          >
                            <h5 v-if="section.heading" class="mb-2 font-medium">
                              {{ section.heading }}
                            </h5>
                            <p v-if="section.content" class="mb-2 text-sm">
                              {{ section.content }}
                            </p>
                            <ul v-if="section.items && section.items.length > 0" class="space-y-1">
                              <li v-for="item in section.items" :key="item" class="flex items-start text-sm">
                                <span
                                  class="mt-2 mr-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-blue-400"
                                ></span>
                                {{ item }}
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <!-- Inspection-specific Sections -->
                      <div
                        v-if="offerte.iplan.inspections"
                        v-for="(inspectionContent, inspectionType) in offerte.iplan.inspections"
                        :key="inspectionType"
                        class="bg-background-50 border-background-200 rounded-sm border p-4"
                      >
                        <h4 class="mb-3 flex items-center text-base font-semibold">
                          <svg
                            class="mr-2 h-4 w-4 text-green-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          {{ inspectionContent?.title || getInspectionTypeLabel(String(inspectionType)) }}
                        </h4>
                        <div v-if="inspectionContent?.sections" class="space-y-3">
                          <div
                            v-for="(section, sectionIndex) in inspectionContent.sections"
                            :key="sectionIndex"
                            class="ml-6"
                          >
                            <h5 v-if="section.heading" class="mb-2 font-medium">
                              {{ section.heading }}
                            </h5>
                            <p v-if="section.content" class="mb-2 text-sm">
                              {{ section.content }}
                            </p>
                            <ul v-if="section.items && section.items.length > 0" class="space-y-1">
                              <li v-for="item in section.items" :key="item" class="flex items-start text-sm">
                                <span
                                  class="mt-2 mr-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-green-400"
                                ></span>
                                {{ item }}
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionPanel>
              </Accordion>
            </div>

            <!-- Status & Price Summary -->
            <div class="border-background-200 bg-background-0 sticky top-10 overflow-hidden rounded-sm border p-4">
              <div class="flex items-start justify-between gap-4">
                <div>
                  <p class="text-muted-color text-xs font-medium">Status</p>
                  <span
                    :class="[
                      'mt-2 inline-flex items-center rounded-sm border px-3 py-1 text-sm font-semibold',
                      statusColor === 'alles_geaccepteerd'
                        ? 'bg-emerald-300 text-gray-800'
                        : statusColor === 'deels_geaccepteerd'
                          ? 'bg-amber-200 text-amber-900'
                          : statusColor === 'foute_inzending' || isRejected
                            ? 'bg-red-300 text-gray-800'
                            : statusColor === 'aangepast'
                              ? 'bg-blue-200 text-blue-900'
                              : 'bg-background-50 border-background-200 text-gray-700',
                    ]"
                  >
                    {{ statusLabel }}
                  </span>
                </div>
                <div class="text-right">
                  <p class="text-muted-color text-xs font-medium">Inspecties</p>
                  <p class="text-muted mt-1 text-sm font-semibold">{{ requiredInspectionTypes.length }} vereist</p>
                </div>
              </div>

              <div
                class="border-background-200 bg-background-300 mt-3 flex flex-col items-center justify-between rounded-md border p-3"
              >
                <div>
                  <p class="text-muted-color text-xs font-bold">Handtekening</p>
                  <div v-if="offerte.handTekening && !isRejected" class="mt-2">
                    <img
                      :src="offerte.handTekening"
                      alt="Handtekening"
                      class="border-background-200 h-28 rounded-sm border bg-white object-contain"
                      loading="lazy"
                    />
                  </div>
                </div>
                <p class="text-muted-color text-xs">
                  {{
                    isRejected
                      ? `Afgewezen op ${formatDateForDisplay(getStatusDate("afgewezen"), true)}`
                      : offerte.handTekening
                        ? `Geaccepteerd op ${formatDateForDisplay(getStatusDate("alles_geaccepteerd"), true)}`
                        : "Nog niet ondertekend"
                  }}
                </p>
              </div>
              <div v-if="!isBundelMember" class="mt-3 space-y-3 rounded-sm border border-slate-200 bg-slate-50 p-3">
                <div class="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p class="text-muted-color text-xs font-medium">{{ submissionTitle }}</p>
                    <p class="mt-1 text-sm font-medium text-slate-800">
                      {{ manualSubmissionLabel || "Nog geen upload" }}
                    </p>
                    <p v-if="requiresRedownload" class="mt-1 text-sm text-amber-700">
                      Er is een nieuw document beschikbaar. Download eerst de laatste PDF voordat u opnieuw uploadt.
                    </p>
                    <p v-else-if="isPartiallyAccepted" class="mt-1 text-sm text-amber-700">
                      Uw upload is ontvangen en wacht op controle door het inspectiebureau.
                    </p>
                    <p v-else-if="hasWrongSubmission" class="mt-1 text-sm text-red-700">
                      De vorige inzending is afgekeurd. Download de laatste PDF, onderteken opnieuw en upload opnieuw.
                    </p>
                  </div>
                  <div class="flex flex-wrap gap-2">
                    <Button label="Download PDF" severity="secondary" outlined @click="downloadCurrentPdf" />
                    <Button
                      v-if="latestSubmission?.uploaded_file_path"
                      label="Bekijk upload"
                      severity="secondary"
                      @click="openUploadedPdf"
                    />
                    <Button
                      v-if="!isAccepted && !isRejected"
                      :label="latestSubmission?.uploaded_file_path ? 'Nieuwe PDF uploaden' : 'Upload getekende PDF'"
                      :loading="uploadingManualPdf"
                      :disabled="!canUploadManualPdf"
                      @click="openUploadPicker"
                    />
                  </div>
                </div>
                <input
                  ref="uploadInput"
                  class="hidden"
                  type="file"
                  accept="application/pdf"
                  @change="handleManualPdfUpload"
                />
                <p v-if="currentDocument?.status !== 'completed'" class="text-sm text-slate-600">
                  Er wordt een nieuw document voorbereid. Uploaden is beschikbaar zodra de PDF gereed is.
                </p>
              </div>
              <div
                v-if="!isAccepted && !isRejected && isBundelMember"
                class="mt-3 rounded-sm border border-blue-200 bg-blue-50 p-4"
              >
                <div class="flex items-start gap-3">
                  <svg
                    class="mt-0.5 h-5 w-5 shrink-0 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div class="flex-1">
                    <p class="mb-1 text-sm font-medium text-blue-800">Onderdeel van bundel "{{ bundelNaam }}"</p>
                    <p class="mb-3 text-sm text-blue-700">
                      Deze offerte kan niet individueel worden ondertekend. Ga naar de bundel om alle offertes tegelijk
                      te accepteren of af te wijzen.
                    </p>
                    <Button
                      label="Ga naar Bundel"
                      icon="pi pi-arrow-right"
                      iconPos="right"
                      size="small"
                      severity="info"
                      @click="router.push({ name: 'BundelDetails', params: { id: offerte.bundel_id } })"
                    />
                  </div>
                </div>
              </div>
              <div v-else-if="!isAccepted && !isRejected" class="mt-3 flex flex-col gap-3 sm:flex-row">
                <Button class="w-full" label="Afwijzen" severity="danger" outlined @click="handleReject" />
                <Button
                  class="w-full"
                  label="Accepteren"
                  icon="pi pi-pen-to-square"
                  severity="primary"
                  @click="openSignatureDialog"
                />
              </div>
            </div>
          </div>
        </div>

        <NotitiesSection :entity-type="'offerte'" :entity-id="offerte?.id" class="mt-4" />

        <Dialog
          v-model:visible="isSignatureDialogOpen"
          modal
          :draggable="false"
          class="w-full max-w-3xl"
          :header="signatureDialogTitle"
        >
          <div v-if="!offerte.handTekening && !isRejected" class="space-y-5">
            <div class="flex items-start justify-between gap-4">
              <div>
                <h2 class="text-lg font-semibold text-gray-900">Ondertekenen</h2>
                <p class="text-sm text-gray-600">Plan de inspecties en accepteer de offerte met een handtekening.</p>
              </div>
              <span
                :class="[
                  'inline-flex items-center rounded-md border px-3 py-1 text-xs font-semibold',
                  allAccepted
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                    : 'bg-background-50 border-background-200 text-gray-700',
                ]"
              >
                {{ allAccepted ? "Klaar voor acceptatie" : "Actie vereist" }}
              </span>
            </div>
            <div v-if="requiredInspectionTypes.length > 0" class="mb-2">
              <h3 class="mb-2 text-base font-semibold text-gray-900">
                {{
                  requiredInspectionTypes.length > 1
                    ? "Selecteer datum per inspectie (kan op dezelfde dag)"
                    : "Selecteer datum voor inspectie"
                }}
                (optioneel)
              </h3>
              <p v-if="requiredInspectionTypes.length > 1" class="mb-4 text-sm text-gray-600">
                {{
                  requiredInspectionTypes.length > 1
                    ? "Stel een datum voor elke inspectie voor."
                    : "Stel een datum voor de inspectie voor."
                }}
              </p>
              <p v-if="requiredInspectionTypes.length === 1">
                Kies een datum uit de beschikbare planning van het inspectiebureau.
              </p>
              <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div
                  v-for="inspectionType in requiredInspectionTypes"
                  :key="inspectionType"
                  class="flex flex-col gap-2"
                >
                  <label
                    :for="`date-${inspectionType}`"
                    class="flex items-center gap-2 text-sm font-medium text-gray-700"
                  >
                    {{ getInspectionTypeLabel(inspectionType) }}
                    <i
                      v-if="selectedInspectionDates[inspectionType]"
                      class="pi pi-check-circle text-sm text-green-500"
                      title="Datum geselecteerd"
                    ></i>
                  </label>
                  <DatePicker
                    :id="`date-${inspectionType}`"
                    v-model="selectedInspectionDates[inspectionType]"
                    :minDate="getMinSelectableDate()"
                    :maxDate="getMaxSelectableDate()"
                    :disabledDates="disabledDatesArray"
                    placeholder="Selecteer datum"
                    showIcon
                    iconDisplay="input"
                    dateFormat="dd/mm/yy"
                    :manualInput="false"
                    fluid
                  >
                    <template #date="slotProps">
                      <div class="relative flex h-full w-full items-center justify-center">
                        <span :class="{ 'text-gray-300': !slotProps.date.selectable }">
                          {{ slotProps.date.day }}
                        </span>
                        <span
                          v-if="slotProps.date.selectable"
                          class="absolute bottom-0 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-green-500"
                        >
                        </span>
                      </div>
                    </template>
                  </DatePicker>
                  <div v-if="selectedInspectionDates[inspectionType]" class="text-xs">
                    <span class="flex items-center gap-1 text-green-600">
                      <i class="pi pi-check"></i>
                      Datum geselecteerd
                    </span>
                  </div>
                </div>
              </div>
              <div v-if="requiredInspectionTypes.length > 1" class="bg-background-50 mt-3 rounded-md border p-3">
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-gray-700"> Voortgang datums selectie </span>
                  <span class="text-sm text-gray-600">
                    {{ Object.values(selectedInspectionDates).filter((date) => date !== null).length }} /
                    {{ requiredInspectionTypes.length }}
                  </span>
                </div>
                <div class="bg-background-200 mt-2 h-2 w-full rounded-full">
                  <div
                    class="h-2 rounded-full transition-all duration-300"
                    :class="{
                      'bg-green-500': Object.values(selectedInspectionDates).filter((date) => date !== null).length > 0,
                      'bg-background-300':
                        Object.values(selectedInspectionDates).filter((date) => date !== null).length === 0,
                    }"
                    :style="{
                      width: `${(Object.values(selectedInspectionDates).filter((date) => date !== null).length / requiredInspectionTypes.length) * 100}%`,
                    }"
                  ></div>
                </div>
              </div>
            </div>
            <div class="border-background-200 bg-background-50 flex items-start gap-3 rounded-md border p-3">
              <Checkbox v-model="allTermsAccepted" inputId="all-terms-acceptance" binary required />
              <label for="all-terms-acceptance">
                Ik accepteer de algemene voorwaarden en inspectieplannen voor alle geselecteerde inspecties.
              </label>
            </div>
            <div class="border-background-200 bg-background-50 rounded-md border p-3">
              <SignaturePad
                v-model="signature"
                :save-disabled="!allAccepted"
                save-button-text="Accepteren"
                clear-pad-text="Wissen"
                :can-edit="true"
                class="aspect-video"
                @save="handleAccept"
                @error="setAcceptanceError"
              />
              <p v-if="acceptanceError" class="mt-2 text-sm text-red-600">
                {{ acceptanceError }}
              </p>
            </div>
          </div>
          <div v-else-if="offerte.handTekening" class="space-y-3">
            <h2 class="text-lg font-semibold text-gray-900">Handtekening</h2>
            <div class="border-background-200 bg-background-50 rounded-md border p-3">
              <SignaturePad v-model="offerte.handTekening" :can-edit="false" />
            </div>
            <p class="text-sm text-gray-600">Geaccepteerd op {{ formatDate(getStatusDate("alles_geaccepteerd")) }}</p>
          </div>
          <div v-else class="border-background-200 bg-background-50 rounded-md border p-3">
            <p class="text-sm text-gray-700">
              Deze offerte is afgewezen op {{ formatDate(getStatusDate("afgewezen")) }}.
            </p>
          </div>
          <template #footer>
            <div class="flex w-full justify-end">
              <Button label="Sluiten" severity="secondary" @click="isSignatureDialogOpen = false" />
            </div>
          </template>
        </Dialog>
      </template>
    </div>
  </DetailsLayout>

  <ConfirmDialog group="rejectOfferte">
    <template #message>
      <div class="flex flex-col gap-2">
        <p>Weet u zeker dat u de offerte wilt <b>afwijzen</b>? Deze actie kan niet meer ongedaan gemaakt worden.</p>
        <p v-if="offerte?.ibedrijf" class="text-color-secondary mt-3 text-sm">
          Heeft u vragen? Neem contact op met
          <span class="font-bold">{{ offerte.ibedrijf.contactnaam || offerte.ibedrijf.users?.display_name }}</span>
          <template v-if="offerte.ibedrijf.telnr">
            via
            <a :href="`tel:${offerte.ibedrijf.telnr}`" class="text-primary underline">{{ offerte.ibedrijf.telnr }}</a>
          </template>
          .
        </p>
      </div>
    </template>
  </ConfirmDialog>
</template>

<script setup lang="ts">
  import { ref, shallowRef, computed, onMounted, onUnmounted, nextTick, watch } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { useOfferteStore } from "../stores/offertes";
  import { useDocumentSubmissions } from "../composables/useDocumentSubmissions";
  import { useToast } from "../composables/useToast";
  import DetailsLayout from "../components/DetailsLayout.vue";
  import SignaturePad from "../components/SignaturePad.vue";
  import AppHeader from "../components/AppHeader.vue";
  import NotitiesSection from "../components/NotitiesSection.vue";
  import Accordion from "primevue/accordion";
  import AccordionPanel from "primevue/accordionpanel";
  import AccordionHeader from "primevue/accordionheader";
  import AccordionContent from "primevue/accordioncontent";
  import DatePicker from "primevue/datepicker";
  import Checkbox from "primevue/checkbox";
  import Dialog from "primevue/dialog";
  import Button from "primevue/button";
  import { useConfirm } from "primevue/useconfirm";
  import ConfirmDialog from "primevue/confirmdialog";

  // PhotoSwipe imports
  // @ts-ignore - PhotoSwipe doesn't have perfect TypeScript support
  import PhotoSwipeLightbox from "photoswipe/lightbox";
  import "photoswipe/style.css";
  import { supabase } from "../lib/supabase";
  import { formatDateForDisplay } from "../utils/dateUtils";
  import { getLatestStatus, getStatusColor, getStatusLabel } from "../utils/offerteStatus";
  import { sanitizeFileName, triggerFileDownload, validateUploadedFile } from "../utils/fileUtils";
  // Import date utilities for planning and validation
  import {
    convertPlanningTimestampsTodates,
    getMinSelectableDate,
    getMaxSelectableDate,
    convertSelectedDatesToStorage,
  } from "../utils/dateUtils";
  // Import VoorwaardeItem type for voorwaarden display (used implicitly in template)
  // @ts-ignore - Type used implicitly in template
  import { inspectieTypeLabels, productTypeLabels, type Offerte, type Product } from "../../../../types";

  const route = useRoute();
  const router = useRouter();
  const offerteStore = useOfferteStore();
  const toast = useToast();
  const confirm = useConfirm();

  const signature = ref<string | undefined>(undefined);
  const acceptanceError = ref("");
  const uploadInput = ref<HTMLInputElement | null>(null);
  const uploadingManualPdf = ref(false);
  const submissionState = shallowRef<ReturnType<typeof useDocumentSubmissions> | null>(null);
  // Single acceptance checkbox for all terms and conditions
  const allTermsAccepted = ref(false);
  const isSignatureDialogOpen = ref(false);

  // Date selection state for inspection scheduling
  const selectedInspectionDates = ref<Record<string, Date | null>>({
    scope8pi: null,
    scope8ebi: null,
    scope10: null,
    scope12pi: null,
    scope12ebi: null,
    nen1010: null,
    nen3140: null,
  });

  const offerte = shallowRef<Offerte | null>(null);
  const beschikbaarTimestamps = ref<string[]>([]);
  const loading = ref(true);
  const error = ref<string | null>(null);

  const latestStatus = computed(() => {
    return getLatestStatus((offerte.value?.status as any[]) || []);
  });

  const isAccepted = computed(() => {
    return latestStatus.value === "alles_geaccepteerd";
  });

  const isPartiallyAccepted = computed(() => latestStatus.value === "deels_geaccepteerd");

  const hasWrongSubmission = computed(() => latestStatus.value === "foute_inzending");

  const isRejected = computed(() => {
    return latestStatus.value === "afgewezen";
  });

  const statusColor = computed(() => getStatusColor(latestStatus.value));

  const statusLabel = computed(() => {
    return getStatusLabel(latestStatus.value);
  });

  const isBundelMember = computed(() => !!offerte.value?.bundel_id);
  const bundelNaam = computed(
    () => offerte.value?.offerte_bundels?.naam || offerte.value?.offerte_bundels?.bundel_nr || "onbekende bundel",
  );

  const signatureDialogTitle = computed(() => {
    if (isAccepted.value) return "Handtekening";
    if (isRejected.value) return "Offerte afgewezen";
    return "Offerte accepteren";
  });

  const fotoBoven = computed(() => offerte.value?.locatie?.fotoBoven_foto?.[0] ?? null);

  // PhotoSwipe refs
  const galleryRef = ref<HTMLDivElement | null>(null);
  const thumbnailRef = ref<HTMLImageElement | null>(null);
  let lightbox: PhotoSwipeLightbox | null = null;

  // Simple scope detection to avoid type complexity
  const hasProductType = (productType: string): boolean => {
    if (!offerte.value?.producten) return false;
    const producten = Array.isArray(offerte.value.producten) ? offerte.value.producten : [];
    return producten.some((product: any) => product.productType === productType);
  };

  // Computed properties for date selection
  const requiredInspectionTypes = computed(() => {
    const types: string[] = [];
    if (hasProductType("scope8pi")) types.push("scope8pi");
    if (hasProductType("scope8ebi")) types.push("scope8ebi");
    if (hasProductType("scope10")) types.push("scope10");
    if (hasProductType("scope12pi")) types.push("scope12pi");
    if (hasProductType("scope12ebi")) types.push("scope12ebi");
    if (hasProductType("nen1010")) types.push("nen1010");
    if (hasProductType("nen3140")) types.push("nen3140");
    return types;
  });

  const availableDates = computed(() => {
    return convertPlanningTimestampsTodates(beschikbaarTimestamps.value);
  });

  // Since PrimeVue DatePicker expects an array of disabled dates,
  // we'll generate a range of dates to disable (all dates except available ones)
  const disabledDatesArray = computed(() => {
    if (!availableDates.value.length) return [];

    // For performance, we'll only disable dates in a reasonable range
    // (e.g., 1 year from today) rather than generating all possible dates
    const disabledDates: Date[] = [];
    const today = new Date();
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(today.getFullYear() + 1);

    // Generate dates from today to one year from now
    const currentDate = new Date(today);
    while (currentDate <= oneYearFromNow) {
      // Check if this date is NOT in available dates
      const isAvailable = availableDates.value.some(
        (availableDate) =>
          availableDate.getFullYear() === currentDate.getFullYear() &&
          availableDate.getMonth() === currentDate.getMonth() &&
          availableDate.getDate() === currentDate.getDate(),
      );

      if (!isAvailable) {
        disabledDates.push(new Date(currentDate));
      }

      // Move to next day
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return disabledDates;
  });

  // Note: Date availability is now handled by the DatePicker's disabledDates prop
  // Users can only select from available dates, so no additional validation needed

  const allAccepted = computed(() => {
    if (isRejected.value) return false;

    // Check single acceptance checkbox
    if (!allTermsAccepted.value) return false;

    return true;
  });

  const openSignatureDialog = () => {
    isSignatureDialogOpen.value = true;
  };

  const latestSubmission = computed(() => submissionState.value?.latestSubmission.value ?? null);
  const currentDocument = computed(() => submissionState.value?.currentDocument.value ?? null);
  const manualSubmissionLabel = computed(() => submissionState.value?.getSubmissionLabel(latestSubmission.value) || "");
  const submissionTitle = computed(
    () => submissionState.value?.getSubmissionTitle(latestSubmission.value?.submission_type) || "Getekende PDF",
  );
  const requiresRedownload = computed(() => submissionState.value?.requiresRedownload.value ?? false);
  const canUploadManualPdf = computed(() =>
    Boolean(submissionState.value?.canUpload.value && !isAccepted.value && !isRejected.value),
  );

  function openUploadPicker() {
    if (!canUploadManualPdf.value || uploadingManualPdf.value) return;
    uploadInput.value?.click();
  }

  async function downloadCurrentPdf() {
    if (!submissionState.value) return;

    try {
      const offerteNr = offerte.value?.offerteNr;
      const fileName = offerteNr ? `${sanitizeFileName(offerteNr)}.pdf` : undefined;
      const signedUrl = await submissionState.value.getPdfSignedUrl(fileName);
      if (!signedUrl) {
        throw new Error("PDF nog niet beschikbaar");
      }
      if (fileName) {
        triggerFileDownload(signedUrl, fileName);
      } else {
        window.open(signedUrl, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      console.error("Error downloading current PDF:", err);
      toast.error(storageErrorMessage(err, "Kon PDF niet downloaden"), err);
    }
  }

  // Storage errors carry raw English API messages ("Object not found"); show
  // something actionable in Dutch instead.
  function storageErrorMessage(err: unknown, fallback: string): string {
    const message = err instanceof Error ? err.message : "";
    if (/object not found|not_found/i.test(message)) {
      return "Het document is niet (meer) beschikbaar. Neem contact op met uw inspectiepartner.";
    }
    return message || fallback;
  }

  async function openUploadedPdf() {
    const filePath = latestSubmission.value?.uploaded_file_path;
    if (!filePath) return;

    try {
      const offerteNr = offerte.value?.offerteNr;
      const fileName = offerteNr ? `${sanitizeFileName(offerteNr)}-getekend.pdf` : undefined;
      const { data, error: signedUrlError } = await supabase.storage
        .from("userFiles")
        .createSignedUrl(filePath, 3600, fileName ? { download: fileName } : undefined);
      if (signedUrlError) throw signedUrlError;
      if (data?.signedUrl) {
        if (fileName) {
          triggerFileDownload(data.signedUrl, fileName);
        } else {
          window.open(data.signedUrl, "_blank", "noopener,noreferrer");
        }
      }
    } catch (err) {
      console.error("Error opening uploaded PDF:", err);
      toast.error(storageErrorMessage(err, "Kon geuploade PDF niet openen"), err);
    }
  }

  async function handleManualPdfUpload(event: Event) {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file || !offerte.value || !submissionState.value) return;

    uploadingManualPdf.value = true;

    try {
      if (requiresRedownload.value) {
        throw new Error("Er is een nieuwer document beschikbaar. Download eerst de laatste PDF.");
      }

      // Validate size + type and derive a SAFE extension/contentType from the
      // allow-list — never trust file.name (path injection) or file.type
      // (spoofed Content-Type on the public bucket) verbatim.
      const validation = validateUploadedFile(file);
      if (!validation.ok) {
        toast.error(validation.error, undefined, { report: false });
        return;
      }

      // UUID-prefixed path so a crafted file.name can never inject `../` segments.
      const filePath = `offertes/${offerte.value.id}/manual-signed-${crypto.randomUUID()}.${validation.value.extension}`;

      const { error: uploadError } = await supabase.storage.from("userFiles").upload(filePath, file, {
        upsert: false,
        contentType: validation.value.contentType,
      });

      if (uploadError) throw uploadError;

      await submissionState.value.createSubmission(filePath, file.name, validation.value.contentType);
      offerte.value = await offerteStore.getOfferteById(offerte.value.id);
      await submissionState.value.load();
      toast.success("Getekende PDF geupload. Het inspectiebureau controleert uw inzending.");
    } catch (err) {
      console.error("Error uploading manual PDF:", err);
      toast.error(err instanceof Error ? err.message : "Kon getekende PDF niet uploaden", err);
    } finally {
      target.value = "";
      uploadingManualPdf.value = false;
    }
  }

  const formatCurrency = (amount: number | null | undefined) => {
    return new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency: "EUR",
    }).format(amount || 0);
  };

  const formatDate = (dateString: string | Date) => {
    return formatDateForDisplay(dateString);
  };

  const getStatusDate = (status: string) => {
    const entry = offerte.value?.status?.find((item: any) => item.status === status);
    return entry?.datum || "";
  };

  // Helper function to get inspection type labels
  const getInspectionTypeLabel = (type: string) => {
    return inspectieTypeLabels[type as keyof typeof inspectieTypeLabels] || type.toUpperCase();
  };

  // Helper function to format numbers (for percentages)
  const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return "0";
    return value.toFixed(2);
  };

  // Helper function to calculate subtotal from aangepast products only
  const getConfirmedSubtotal = (): number => {
    if (!offerte.value?.producten) return 0;

    return offerte.value.producten
      .filter((product: any) => product.prijsType === "aangepast")
      .reduce((total: number, product: any) => {
        const prijs = product.prijs || 0;
        const aantal = product.aantal || 1;
        return total + prijs * aantal;
      }, 0);
  };

  // Helper function to get confirmed products for display
  const getConfirmedProducts = (): any[] => {
    if (!offerte.value?.producten) return [];

    return offerte.value.producten
      .filter((product: any) => product.prijsType === "aangepast")
      .sort((a: any, b: any) => {
        // Sort km products last, then alphabetically
        if (a.productType === "km" && b.productType !== "km") return 1;
        if (a.productType !== "km" && b.productType === "km") return -1;
        return (a.productType || "").localeCompare(b.productType || "");
      });
  };

  // Helper function to get product label for display
  const getProductLabel = (productType: string): string => {
    return productTypeLabels[productType as keyof typeof productTypeLabels] || productType.toUpperCase();
  };

  // Helper function to get VAT amount
  const getVatAmount = (): number => {
    const subtotal = getConfirmedSubtotal();
    if (subtotal === 0) return 0;

    // Use totaalIncKorting if discount is applied, otherwise use confirmed subtotal
    const baseAmount =
      offerte.value?.korting && offerte.value.totaalIncKorting !== null && offerte.value.totaalIncKorting !== undefined
        ? offerte.value.totaalIncKorting
        : subtotal;

    return baseAmount * 0.21;
  };

  // Helper function to get final total including VAT
  const getFinalTotal = (): number => {
    const subtotal = getConfirmedSubtotal();
    if (subtotal === 0) return 0;

    // Use totaalIncKorting if discount is applied, otherwise use confirmed subtotal
    const baseAmount =
      offerte.value?.korting && offerte.value.totaalIncKorting !== null && offerte.value.totaalIncKorting !== undefined
        ? offerte.value.totaalIncKorting
        : subtotal;

    return baseAmount * 1.21;
  };

  const setAcceptanceError = (error: string) => {
    acceptanceError.value = error;
  };

  const isAccepting = ref(false);

  const handleAccept = async (signatureData: string) => {
    if (!offerte.value || isAccepting.value) return;
    isAccepting.value = true;
    try {
      acceptanceError.value = "";

      // Convert signature to blob
      const signatureBlob = await fetch(signatureData).then((res) => res.blob());

      // Create file path according to storage structure: offertes/{offerte_id}/handtekening.{extension}
      const timestamp = new Date().getTime();
      const filePath = `offertes/${offerte.value.id}/handtekening.png`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage.from("userFiles").upload(filePath, signatureBlob, {
        contentType: "image/png",
        upsert: true,
      });

      if (uploadError) throw uploadError;

      // Get public URL and add cache-busting parameter
      const {
        data: { publicUrl },
      } = supabase.storage.from("userFiles").getPublicUrl(filePath);

      const urlWithCacheBuster = `${publicUrl}?t=${timestamp}`;

      // Convert selected dates to storage format
      const inspectionDatesForStorage = convertSelectedDatesToStorage(selectedInspectionDates.value);

      // Accept offerte with signature and inspection dates. Setting the
      // handtekening triggers server-side regeneration of the offerte PDF
      // (trg_offertes_regenerate_document_on_handtekening).
      await offerteStore.acceptOfferte(offerte.value.id, urlWithCacheBuster, inspectionDatesForStorage);

      offerte.value = await offerteStore.getOfferteById(offerte.value.id);
      toast.success("Offerte succesvol geaccepteerd");
      isSignatureDialogOpen.value = false;
    } catch (error: any) {
      console.error("Error accepting offerte:", error);
      toast.error("Er is een fout opgetreden bij het accepteren van de offerte", error);
    } finally {
      isAccepting.value = false;
    }
  };

  const handleReject = () => {
    if (!offerte.value) return;

    confirm.require({
      group: "rejectOfferte",
      header: "Offerte afwijzen",
      icon: "pi pi-exclamation-triangle",
      rejectLabel: "Annuleren",
      rejectProps: { severity: "secondary" },
      acceptLabel: "Afwijzen",
      acceptProps: { severity: "danger" },
      accept: async () => {
        try {
          await offerteStore.rejectOfferte(offerte.value!.id);
          offerte.value = await offerteStore.getOfferteById(offerte.value!.id);
          toast.success("Offerte afgewezen");
          isSignatureDialogOpen.value = false;
        } catch (rejectError) {
          console.error("Error rejecting offerte:", rejectError);
          toast.error("Er is een fout opgetreden bij het afwijzen van de offerte", rejectError);
        }
      },
    });
  };

  // Watcher for date selection changes to provide real-time feedback
  watch(
    selectedInspectionDates,
    (newDates: Record<string, Date | null>) => {
      // Log date changes for debugging
      console.log("Selected inspection dates updated:", newDates);
    },
    { deep: true },
  );

  // PhotoSwipe initialization and management
  const initPhotoSwipe = () => {
    if (lightbox) {
      lightbox.destroy();
      lightbox = null;
    }

    // Use a string selector instead of a DOM reference
    const galleryId = "pswp-gallery-" + Math.random().toString(36).substring(2, 11);

    // Set the ID on the gallery element
    if (galleryRef.value) {
      galleryRef.value.id = galleryId;
    }

    // Create the lightbox with a string selector
    lightbox = new PhotoSwipeLightbox({
      gallery: "#" + galleryId,
      children: "a",

      // PhotoSwipe options for better UX
      closeOnVerticalDrag: true, // Enable swipe-to-go-back
      pinchToClose: true, // Enable pinch-to-close
      wheelToZoom: true, // Enable mouse wheel zoom

      // Animation settings for smooth thumbnail transition
      showAnimationDuration: 300,
      hideAnimationDuration: 250,

      // Use thumbEl filter to get the visible thumbnail
      // This is crucial for the animation to work properly
      thumbSelector: ".hero-thumbnail",

      // Dynamic import of PhotoSwipe core - using package export
      // @ts-ignore - PhotoSwipe doesn't have perfect TypeScript support
      pswpModule: () => import("photoswipe"),
    });

    // Add a filter to use the visible thumbnail for animation
    lightbox.addFilter("thumbEl", (_thumbEl: any, _data: any) => {
      // Return the visible thumbnail element, or fallback to the original thumbEl
      return thumbnailRef.value || _thumbEl;
    });

    lightbox.init();
  };

  // Get image dimensions for better PhotoSwipe animation
  const getImageDimensions = async (src: string): Promise<{ width: number; height: number }> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.naturalWidth || 1920,
          height: img.naturalHeight || 1080,
        });
      };
      img.onerror = () => {
        // Default dimensions if image can't be loaded
        resolve({
          width: 1920,
          height: 1080,
        });
      };
      img.src = src;
    });
  };

  // Update gallery link with actual image dimensions
  const updateGalleryDimensions = async () => {
    if (!fotoBoven.value?.remote_url || !galleryRef.value) return;

    try {
      const dimensions = await getImageDimensions(fotoBoven.value.remote_url);
      const link = galleryRef.value.querySelector("a");
      if (link) {
        link.setAttribute("data-pswp-width", dimensions.width.toString());
        link.setAttribute("data-pswp-height", dimensions.height.toString());
      }
    } catch (err) {
      console.error("Failed to get image dimensions:", err);
    }
  };

  // Open image modal function
  const openImageModal = async () => {
    if (!fotoBoven.value?.remote_url) {
      console.log("Cannot open image: missing fotoBoven");
      return;
    }

    if (!lightbox) {
      await nextTick();
      await updateGalleryDimensions();
      initPhotoSwipe();
    }

    if (!lightbox) {
      console.log("Cannot open image: lightbox not initialized");
      return;
    }

    try {
      // PhotoSwipe will automatically animate from the thumbnail
      lightbox.loadAndOpen(0);
    } catch (error) {
      console.error("Error opening PhotoSwipe:", error);
    }
  };

  watch(
    () => fotoBoven.value?.remote_url,
    async (newUrl) => {
      if (!newUrl) return;
      await nextTick();
      await updateGalleryDimensions();
      initPhotoSwipe();
    },
  );

  onMounted(async () => {
    console.log("[OfferteDetails] Component mounted, route params:", route.params);
    try {
      const id = Array.isArray(route.params.id) ? route.params.id[0] : route.params.id;
      console.log("[OfferteDetails] Fetching offerte with id:", id);
      offerte.value = await offerteStore.getOfferteById(id);
      console.log("[OfferteDetails] getOfferteById returned:", offerte.value ? "data" : "null");
      if (!offerte.value) {
        error.value = "Offerte niet gevonden";
        console.log("[OfferteDetails] Offerte not found");
        return;
      }
      // Track that the opdrachtgever opened this offerte. Bundel members are
      // covered by mark_bundel_bekeken from BundelDetails, so skip them here.
      if (!offerte.value.bundel_id) {
        void offerteStore.markOfferteBekeken(offerte.value.id);
      }
      const ibedrijfId = offerte.value.ibedrijf?.id;
      if (ibedrijfId) {
        void offerteStore.fetchIbedrijfBeschikbaarheid(ibedrijfId).then((timestamps) => {
          beschikbaarTimestamps.value = timestamps;
        });
      }
      const state = useDocumentSubmissions("offerte", offerte.value.id);
      submissionState.value = state;
      await state.load();
      console.log("Offerte loaded:", {
        handTekening: offerte.value.handTekening,
        fullOfferte: offerte.value,
      });
    } catch (e) {
      console.error("Error loading offerte:", e);
      error.value = "Er is een fout opgetreden bij het laden van de offerte";
    } finally {
      loading.value = false;
    }
  });

  onUnmounted(() => {
    // Clean up PhotoSwipe instance
    if (lightbox) {
      lightbox.destroy();
      lightbox = null;
    }
  });
</script>

<style scoped>
  /* Keep only essential styles that can't be replaced with Tailwind */

  /* Hide the PhotoSwipe gallery but keep it accessible */
  .pswp-gallery {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    margin: -1px;
    padding: 0;
    border: 0;
  }

  .signature-pad-container {
    border: 1px solid #ddd;
    border-radius: 4px;
    margin-bottom: 1rem;
    background: white;
    width: 100%;
    height: 200px;
    position: relative;
    touch-action: none;
  }

  .signature-pad-container canvas {
    width: 100% !important;
    height: 100% !important;
    position: absolute;
    top: 0;
    left: 0;
    touch-action: none;
    cursor: crosshair;
  }

  .link-button {
    background: none;
    border: none;
    color: #007bff;
    text-decoration: underline;
    cursor: pointer;
    padding: 0;
    margin-left: 0.5rem;
  }

  .link-button:hover {
    color: #0056b3;
  }
</style>
